import { Fragment, ReactElement, useEffect, useState } from "react";
import {
  Anchor,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import ErrorMessage from "@common/components/text/ErrorMessage";
import { purchasesStore } from "@stores/purchases";
import { getInstancePathname, getInstanceUrl } from "@stores/instances/utils";
import {
  formatPlanShortName,
  isDevPlan,
  isProPlan,
  isSelfHostedPlan,
  isStarterPlan,
  isYearlyPlan,
} from "@common/utils/plan";
import IconPlus from "@common/components/icons/IconPlus";
import Tick from "@common/components/icons/Tick";
import IconCross from "@common/components/icons/IconCross";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import SwitchWithLabels from "@common/components/ui/form/SwitchWithLabels";
import IconSpeechBubble from "@common/components/icons/IconSpeechBubble";
import ConfirmationModal from "@components/account/plans/ConfirmationModal";
import StripeProvider from "@components/thirdParty/stripe/StripeProvider";
import BannerSOC2 from "@common/components/icons/BannerSOC2";
import TextWithTooltip from "@common/components/ui/interactive/TextWithTooltip";
import WarningCard from "@common/components/thirdParty/mantine/card/WarningCard";
import { useRouter } from "next/router";
import Link from "next/link";
import { useAccount } from "@stores/session/hooks";
import { getSubscriptionStorageAddOn } from "@stores/subscriptions/utils";
import { formatQuantity } from "@common/utils/string";
import { useAddOnsByType } from "@stores/addons/hooks";
import { usePlan, usePlans, usePlansHierarchy } from "@stores/plans/hooks";
import {
  PLAN_ALIAS_PRO_CLOUD,
  PLAN_ALIAS_STARTER,
} from "@stores/plans/constants";
import { getParentPlanAlias } from "@stores/plans/utils";
import PageTitle from "@common/components/thirdParty/mantine/text/PageTitle";
import DisabledContainer from "@common/components/layout/DisabledContainer";

export default function ManagePlansPage(): ReactElement {
  const theme = useMantineTheme();
  const smScreen = useMediaQuery(`(min-width: ${theme.breakpoints.sm}px)`);
  const ColumnWrapperComponent = smScreen ? Box : Card;

  const router = useRouter();
  const { query } = router;
  const { plan } = query;

  // account
  const account = useAccount();

  // purchases
  const {
    isLoading: loadingPurchases,
    error: purchasesError,
    data: purchases = [],
  } = purchasesStore.useGetPurchases();

  const [firstPurchase] = purchases;
  const isFirstPurchaseSelfHosted =
    firstPurchase && isSelfHostedPlan(firstPurchase.product.plan.alias);
  const isFirstPurchaseYearly =
    firstPurchase && isYearlyPlan(firstPurchase.product.plan.alias);

  // new plan
  const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedPlanAlias, setSelectedPlanAlias] =
    useState(PLAN_ALIAS_PRO_CLOUD);
  const [isYearly, setIsYearly] = useState(false);
  const billingFrequencyText = isYearly ? "year" : "month";
  const [isReady, setIsReady] = useState(false);

  // storage
  const subscriptionStorageAddOn =
    firstPurchase && getSubscriptionStorageAddOn(firstPurchase.subscription);
  // resolved on the first purchase's deployment, so a self-hosted purchase
  // carrying legacy prepaid storage quotes no row
  const storageAddOn = useAddOnsByType({
    isCloudHosted: !isFirstPurchaseSelfHosted,
    isYearly,
  }).dwh;

  // plans
  const allPlans = usePlans();
  const starterPlan = usePlan({ alias: PLAN_ALIAS_STARTER })!;
  const parentPlanOffers = usePlansHierarchy({
    yearly: isYearly,
    dev: firstPurchase && isDevPlan(firstPurchase.product.plan.alias),
  });
  const planOffers = parentPlanOffers
    .map((parentPlanOffer) =>
      parentPlanOffer.plans.filter(
        (planOffer) =>
          isStarterPlan(planOffer.alias) ||
          isFirstPurchaseSelfHosted === isSelfHostedPlan(planOffer.alias),
      ),
    )
    .flat();

  const selectedPlan = usePlan({ alias: selectedPlanAlias });

  // prefill
  useEffect(() => {
    if (firstPurchase && !isReady && allPlans) {
      const formattedPlan = plan && String(plan);

      setIsYearly(
        isYearlyPlan(formattedPlan || firstPurchase.product.plan.alias),
      );

      setSelectedPlanAlias(
        formattedPlan ||
          allPlans
            .filter(({ alias }) => !alias.includes("-with-dwh")) // legacy DWH plans
            .find(
              ({ alias }) =>
                getParentPlanAlias(alias) ===
                getParentPlanAlias(firstPurchase.product.plan.alias),
            )?.alias ||
          firstPurchase.product.plan.alias,
      );

      setIsReady(true);
    }
  }, [isReady, firstPurchase, allPlans, plan]);

  return (
    <Container>
      {loadingPurchases && <Loader />}

      <ErrorMessage error={purchasesError} />

      {selectedPlan ? (
        <>
          {!loadingPurchases &&
            !purchasesError &&
            (firstPurchase ? (
              <Stack spacing="lg">
                <Stack spacing="xs" ta="center">
                  <PageTitle m={0} ta="center">
                    Choose the right plan for you
                  </PageTitle>
                  {firstPurchase.hosted_instance && (
                    <Text fz="sm" m={0}>
                      Your Metabase instance:{" "}
                      <Anchor
                        href={getInstanceUrl(firstPurchase.hosted_instance)}
                        target="_blank"
                        rel="noreferrer"
                        fw={700}
                      >
                        {getInstancePathname(firstPurchase.hosted_instance)}
                      </Anchor>
                    </Text>
                  )}
                </Stack>

                <Card
                  withBorder={smScreen}
                  bg={smScreen ? undefined : "transparent"}
                  p={0}
                  w="100%"
                >
                  <Box
                    display={{ base: "none", sm: "block" }}
                    p="md"
                    ta="center"
                  >
                    <SwitchWithLabels
                      data-testid="ManagePlansPage-billing-frequency"
                      disabled={isFirstPurchaseYearly}
                      labelOff="Billing monthly"
                      labelOn="Billing annually"
                      badge="Save 10%"
                      value={isYearly}
                      onChange={setIsYearly}
                      size="md"
                      fz="sm"
                      m="auto"
                      w="auto"
                    />
                  </Box>

                  <Divider
                    color="gray.2"
                    display={{ base: "none", sm: "block" }}
                    w="100%"
                  />

                  <Flex
                    align="flex-start"
                    direction={{ base: "column", sm: "row" }}
                    gap={{ base: "lg", sm: 0 }}
                    justify="stretch"
                    wrap="nowrap"
                    bg={
                      smScreen
                        ? isStarterPlan(selectedPlanAlias)
                          ? "gray.0"
                          : "#ffffff"
                        : "transparent"
                    }
                    w="100%"
                  >
                    {planOffers.map((planOffer, index) => {
                      const {
                        alias,
                        short_name,
                        description,
                        descriptionAlt,
                        price,
                        users_included,
                        per_user_price,
                        includes,
                        includesAlt,
                      } = planOffer;

                      const isSelectedPlan = alias.includes(selectedPlanAlias);
                      const isStarterPlan = alias.includes(PLAN_ALIAS_STARTER);
                      const isPro = isProPlan(alias);
                      const isSelfHosted = isSelfHostedPlan(alias);
                      const color = isStarterPlan
                        ? theme.colors.green[1]
                        : theme.colors.brand[4];

                      const disabled =
                        isFirstPurchaseSelfHosted && !isSelfHosted;

                      // includes
                      const theIncludes = includesAlt || includes;
                      const starterIncludes =
                        starterPlan.includesAlt || starterPlan.includes;
                      const starterPlanIncludesDescriptions =
                        starterIncludes.map(({ description }) => description);
                      const planIncludesDescriptions = !isStarterPlan
                        ? theIncludes.filter(
                            ({ description }) =>
                              !starterPlanIncludesDescriptions.includes(
                                description,
                              ),
                          )
                        : theIncludes;

                      return (
                        <Fragment key={index}>
                          {index > 0 && (
                            <Divider
                              orientation="vertical"
                              color="gray.2"
                              display={{ base: "none", sm: "block" }}
                            />
                          )}

                          <ColumnWrapperComponent
                            data-testid={alias}
                            bg={isSelectedPlan ? "gray.0" : "#ffffff"}
                            p={0}
                            w="100%"
                            // HACK
                            // So withBorder is for Card only
                            {...(smScreen ? {} : { withBorder: true })}
                            //
                          >
                            <DisabledContainer
                              data-testid={`${getParentPlanAlias(alias)}_disabled-container`}
                              disabled={disabled}
                            >
                              <Box
                                display={{ base: "block", sm: "none" }}
                                p="md"
                                ta="center"
                              >
                                <SwitchWithLabels
                                  disabled={isFirstPurchaseYearly || disabled}
                                  labelOff="Billing monthly"
                                  labelOn="Billing annually"
                                  badge="Save 10%"
                                  value={isYearly}
                                  onChange={setIsYearly}
                                  m="auto"
                                  w="auto"
                                />
                              </Box>

                              <Divider
                                color="gray.2"
                                display={{ base: "block", sm: "none" }}
                                w="100%"
                              />

                              <Box p="xl" pos="relative">
                                {isPro && (
                                  <Box pos="absolute" right={0} top={0}>
                                    <BannerSOC2 />
                                  </Box>
                                )}

                                <Title
                                  data-testid={`PlanTitle-${getParentPlanAlias(alias)}`}
                                  order={2}
                                  c={color}
                                  fz="xl"
                                  m={0}
                                  w="100%"
                                >
                                  {formatPlanShortName(short_name)}
                                  {subscriptionStorageAddOn?.prepaid_units
                                    ? " + Storage"
                                    : ""}
                                </Title>
                                <Text
                                  c="gray.4"
                                  fz="md"
                                  m={0}
                                  mt={theme.spacing.xs / 2}
                                  w={isPro ? "85%" : "100%"}
                                >
                                  {descriptionAlt || description}
                                </Text>

                                <Group
                                  noWrap
                                  spacing={theme.spacing.xs / 2}
                                  c="gray.5"
                                  mt="md"
                                >
                                  <Group
                                    align="flex-end"
                                    noWrap
                                    spacing={theme.spacing.xs / 2}
                                  >
                                    <Text fw={700} fz="xl" m={0}>
                                      ${price}
                                    </Text>
                                    <Text fw={700} fz="sm" mb={2}>
                                      /{billingFrequencyText}
                                    </Text>
                                  </Group>
                                  <IconPlus color={theme.colors.gray[5]} />
                                  <Group
                                    align="flex-end"
                                    noWrap
                                    spacing={theme.spacing.xs / 2}
                                  >
                                    <Text fw={700} fz="xl" m={0}>
                                      ${per_user_price}
                                    </Text>
                                    <Text fw={700} fz="sm" mb={2}>
                                      /{billingFrequencyText} per user
                                    </Text>
                                  </Group>
                                </Group>
                                <TextWithTooltip
                                  color="gray.5"
                                  fz="xs"
                                  lh={1.3333}
                                  m={0}
                                  pr="md"
                                  content={`First [${users_included} users](An user account is any account which has been added to your Metabase instance (manually or via SSO) that has not been deactivated.) included`}
                                />

                                {subscriptionStorageAddOn && storageAddOn && (
                                  <Group
                                    align="flex-end"
                                    noWrap
                                    spacing={theme.spacing.xs / 2}
                                    mt={theme.spacing.xs / 2}
                                  >
                                    <Text fw={700} fz="xl">
                                      {formatQuantity(
                                        subscriptionStorageAddOn.prepaid_units,
                                      )}{" "}
                                      rows for $
                                      {Math.round(
                                        storageAddOn.default_price_per_unit *
                                          subscriptionStorageAddOn.prepaid_units,
                                      )}
                                    </Text>
                                    <Text fw={700} fz="sm" mb={2}>
                                      /{billingFrequencyText}
                                    </Text>
                                  </Group>
                                )}

                                <Button
                                  disabled={disabled}
                                  onClick={() => {
                                    setSelectedPlanAlias(planOffer.alias);
                                    setTimeout(
                                      openModal,
                                      theme.other.animation.duration
                                        .interaction,
                                    );
                                  }}
                                  variant={
                                    isSelectedPlan ? undefined : "outline"
                                  }
                                  mt="md"
                                  w="100%"
                                >
                                  Choose {formatPlanShortName(short_name)}
                                </Button>

                                <Group
                                  align="center"
                                  noWrap
                                  spacing={theme.spacing.xs / 2}
                                  mt="lg"
                                  w="100%"
                                >
                                  <IconSpeechBubble color={color} />
                                  <Text fz="sm">3-day email support</Text>
                                </Group>
                              </Box>
                              <Divider color="gray.2" w="100%" />
                              <Box p="xl">
                                <Group spacing="sm" w="100%">
                                  <Text fz="sm">
                                    {isStarterPlan ? (
                                      "Choose if you want:"
                                    ) : (
                                      <>
                                        Choose if you want everything in{" "}
                                        <b>Starter</b> plus:
                                      </>
                                    )}
                                  </Text>
                                  {planIncludesDescriptions
                                    .filter(({ checked }) => checked)
                                    .map(({ description }, index) => (
                                      <Group
                                        key={index}
                                        align="flex-start"
                                        noWrap
                                        spacing={theme.spacing.xs / 2}
                                        w="100%"
                                      >
                                        <Box fz={0} miw={15} mt={7}>
                                          <Tick color={theme.colors.gray[5]} />
                                        </Box>
                                        <TextWithTooltip
                                          fz="sm"
                                          ml="xs"
                                          content={description}
                                        />
                                      </Group>
                                    ))}
                                </Group>
                                {isStarterPlan && (
                                  <Group spacing="sm" mt="xl" w="100%">
                                    <Text fz="sm">
                                      If you choose <b>Starter</b>, certain
                                      features will become{" "}
                                      <Text span c="red.3" fw={700}>
                                        unavailable
                                      </Text>
                                      :
                                    </Text>
                                    {theIncludes
                                      .filter(({ checked }) => !checked)
                                      .map(({ description }, index) => (
                                        <Group
                                          key={index}
                                          align="flex-start"
                                          noWrap
                                          spacing={theme.spacing.xs / 2}
                                          w="100%"
                                        >
                                          <Box fz={0} miw={15} mt={7}>
                                            <IconCross
                                              color={theme.colors.red[3]}
                                            />
                                          </Box>
                                          <TextWithTooltip
                                            fz="sm"
                                            ml="xs"
                                            content={description}
                                          />
                                        </Group>
                                      ))}
                                  </Group>
                                )}
                              </Box>
                            </DisabledContainer>
                          </ColumnWrapperComponent>
                        </Fragment>
                      );
                    })}
                  </Flex>
                </Card>

                <Card withBorder bg="gray.0" px="xl" py="md">
                  <Flex
                    align={{ base: "flex-start", sm: "center" }}
                    direction="row"
                    gap="xs"
                    justify="flex-start"
                    w="100%"
                  >
                    <Box miw={24}>
                      <IconSpeechBubble />
                    </Box>
                    <Flex
                      align="center"
                      direction="row"
                      gap="md"
                      justify="space-between"
                      wrap="nowrap"
                      w="100%"
                    >
                      <Text fw={700} fz="sm">
                        Not sure which plan to choose?
                        <Text display={{ base: "none", sm: "inline" }} span>
                          &nbsp;
                        </Text>
                        <Text display={{ base: "inline", sm: "none" }} span>
                          <br />
                        </Text>
                        We&apos;d be happy to help.
                      </Text>
                      <Anchor href="mailto:help@metabase.com" fw={700} fz="sm">
                        Contact us
                      </Anchor>
                    </Flex>
                  </Flex>
                </Card>
              </Stack>
            ) : (
              <Stack spacing="sm">
                <PageTitle m={0}>Are you lost?</PageTitle>

                <Text fz="sm">
                  Your instances can be found{" "}
                  <Anchor component={Link} href="/account">
                    here
                  </Anchor>
                  .
                </Text>

                <WarningCard>
                  <Text fz="sm">
                    If you&apos;re having any difficulties, don&apos;t hesitate
                    to write to{" "}
                    <Anchor href="mailto:help@metabase.com">
                      help@metabase.com
                    </Anchor>
                    .
                  </Text>
                </WarningCard>
              </Stack>
            ))}

          {account && selectedPlan && firstPurchase && (
            <StripeProvider>
              <ConfirmationModal
                account={account}
                plan={selectedPlan}
                purchase={firstPurchase}
                isYearly={isYearly}
                opened={opened}
                onClose={closeModal}
              />
            </StripeProvider>
          )}
        </>
      ) : (
        <WarningCard withWarningIcon>
          <Text fz="sm">
            Your{" "}
            {firstPurchase && (
              <>
                <b>{firstPurchase.product.plan.billing_details_name}</b>{" "}
              </>
            )}
            plan is not available to <b>pay now</b>, please contact the{" "}
            <Anchor href="mailto:help@metabase.com">Metabase support</Anchor>.
          </Text>
        </WarningCard>
      )}
    </Container>
  );
}
